import {
  createContext,
  createElement,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import axios from 'axios'
import {toast} from 'react-toastify'

import {useAuth} from '../../modules/auth'
import {URL} from '../gql/client'
import {formatPhoneNumber} from '../helpers'
import {STORAGE_NAMES} from '../helpers/consts'
import {
  TelephonyCampaignOption,
  TelephonyInboundGroupOption,
  TELEPHONY_DRAWER_CLOSE_EVENT,
  TELEPHONY_DRAWER_OPEN_EVENT,
  TelephonyDrawerOpenDetail,
  TELEPHONY_DRAWER_UPDATE_EVENT,
  TelephonyDrawerDiagnostics,
  TELEPHONY_VICIDIAL_LOGOUT_EVENT,
  TELEPHONY_VICIDIAL_DISPOSITION_EVENT,
  TELEPHONY_VICIDIAL_PREPARE_DIAL_EVENT,
  TELEPHONY_VICIDIAL_SESSION_EVENT,
  TelephonyDisconnectResponse,
  TelephonyHangupResponse,
  TelephonyProvider as TelephonyProviderType,
  TelephonyResolveResponse,
  TelephonyStatusResponse,
  TelephonyStartResponse,
  TelephonyVicidialActionResponse,
  TelephonyVicidialDispositionDetail,
  TelephonyVicidialPrepareDialDetail,
  TelephonyVicidialSessionDetail,
  TelephonyWebphoneResponse,
} from '../types/telephony'

const DEFAULT_TELEPHONY_CONFIG: TelephonyResolveResponse = {
  provider: 'RINGCENTRAL',
  telephony_provider: 'RINGCENTRAL',
  source: 'unknown',
  vicidial_enabled: false,
  vicidial_base_url: '',
  vicidial_agent_user: '',
  vicidial_phone_login: '',
  vicidial_campaign_id: '',
  vicidial_auto_login_configured: false,
  vicidial_webphone_configured: false,
  vicidial_supervisor_access: false,
  ringcentral_enabled: true,
}

interface StartTelephonyCallInput {
  phone: string
  leadId?: number | string | null
  autoOpenWebphone?: boolean
  providerOverride?: TelephonyProviderType
}

interface OpenWebphoneOptions {
  preferAgentSession?: boolean
  allowExternalFallback?: boolean
  phoneOnly?: boolean
}

interface GetAgentStatusOptions {
  silent?: boolean
}

interface VicidialSessionState {
  connected: boolean
  connecting: boolean
  activeSession: boolean
  agentUser: string
  phoneLogin: string
  selectedCampaignId: string
  status: string
  callId: string
  localCallId: number | null
  leadId: string
  campaignId: string
  pauseCode: string
  realTimeSubStatus: string
  sessionId: string
  phoneNumber: string
  vendorLeadCode: string
  firstName: string
  lastName: string
  email: string
  comments: string
  callsInQueueCount: number
  callStartedAt: number | null
  wrapUpStartedAt: number | null
  message: string
  rawResponse: string
  checkedAt: number | null
}

interface SetVicidialDispositionInput {
  statusCode: string
  callbackDateTime?: string
  callbackComments?: string
  callbackType?: string
  qmDispoCode?: string
}

interface TelephonyConnectAgentResponse {
  provider: TelephonyProviderType
  agent_user?: string
  active_session?: boolean
  raw_response?: string
  status?: string
  campaign_id?: string
  session_id?: string
  phone_number?: string
}

const dispatchTelephonyDrawer = (detail: TelephonyDrawerOpenDetail) => {
  window.dispatchEvent(
    new CustomEvent<TelephonyDrawerOpenDetail>(TELEPHONY_DRAWER_OPEN_EVENT, {detail})
  )
}

const dispatchTelephonyDrawerClose = () => {
  window.dispatchEvent(new CustomEvent(TELEPHONY_DRAWER_CLOSE_EVENT))
}

const dispatchTelephonyDrawerDiagnostics = (diagnostics: TelephonyDrawerDiagnostics) => {
  window.dispatchEvent(
    new CustomEvent<{diagnostics: TelephonyDrawerDiagnostics}>(TELEPHONY_DRAWER_UPDATE_EVENT, {
      detail: {diagnostics},
    })
  )
}

const dispatchVicidialPrepareDial = (detail: TelephonyVicidialPrepareDialDetail) => {
  window.dispatchEvent(
    new CustomEvent<TelephonyVicidialPrepareDialDetail>(TELEPHONY_VICIDIAL_PREPARE_DIAL_EVENT, {
      detail,
    })
  )
}

const dispatchVicidialSessionState = (detail: TelephonyVicidialSessionDetail) => {
  window.dispatchEvent(
    new CustomEvent<TelephonyVicidialSessionDetail>(TELEPHONY_VICIDIAL_SESSION_EVENT, {
      detail,
    })
  )
}

const dispatchVicidialDisposition = (detail: TelephonyVicidialDispositionDetail) => {
  window.dispatchEvent(
    new CustomEvent<TelephonyVicidialDispositionDetail>(TELEPHONY_VICIDIAL_DISPOSITION_EVENT, {
      detail,
    })
  )
}

const delay = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms))
const VICIDIAL_WAITING_CONFIRMATION_MESSAGE =
  'Waiting for PolicyDial confirmation. The webphone is open, but the agent session is not active yet.'
const VICIDIAL_UNVERIFIED_MESSAGE =
  'The webphone is open and registering in the browser, but the local backend cannot verify PolicyDial status right now.'
const VICIDIAL_LOGGED_OUT_MESSAGE = 'PolicyDial is disconnected in this browser.'
const VICIDIAL_EXTERNAL_WINDOW_KEY = '__policytoolsVicidialWebphoneWindow'

const isVicidialForceDisconnected = () =>
  Boolean(localStorage.getItem(STORAGE_NAMES.VICIDIAL_FORCE_DISCONNECTED))

const isVicidialStatusConnectivityError = (message?: string | null) => {
  const value = String(message || '').toLowerCase()
  return (
    value.includes('vicidial agent status request failed') ||
    value.includes('connecttimeouterror') ||
    value.includes('httpconnectionpool') ||
    value.includes('/vicidial/non_agent_api.php')
  )
}

const isVicidialLoggedOutStatus = (status: TelephonyStatusResponse | null) => {
  const statusValue = String(status?.status || '')
    .trim()
    .toUpperCase()
  const rawValue = String(status?.raw_response || '').toLowerCase()
  return statusValue === 'LOGOUT' || rawValue.includes('agent not logged in')
}

const hasMeaningfulVicidialValue = (value?: string | null) => {
  const normalized = String(value || '')
    .trim()
    .toUpperCase()
  return Boolean(normalized && !['0', 'NONE', 'NULL', 'N/A'].includes(normalized))
}

const isVicidialDispositionPendingState = (
  status?: string | null,
  realTimeSubStatus?: string | null
) => {
  const statusValue = String(status || '')
    .trim()
    .toUpperCase()
  const subStatusValue = String(realTimeSubStatus || '')
    .trim()
    .toUpperCase()
  return (
    statusValue === 'DEAD' ||
    subStatusValue === 'DEAD' ||
    subStatusValue.includes('DEAD') ||
    statusValue === 'DISPO' ||
    subStatusValue === 'DISPO'
  )
}

const clearVicidialCallContextIfIdle = (
  status: TelephonyStatusResponse | null
): TelephonyStatusResponse | null => {
  if (!status) return status

  if (status.policytools_call_completed) {
    return {
      ...status,
      status: 'DEAD',
      call_id: '',
      lead_id: '',
      phone_number: '',
      vendor_lead_code: '',
      real_time_sub_status: 'DEAD',
    }
  }

  const statusValue = String(status.status || '')
    .trim()
    .toUpperCase()
  const subStatusValue = String(status.real_time_sub_status || '')
    .trim()
    .toUpperCase()
  if (isVicidialDispositionPendingState(statusValue, subStatusValue)) {
    return {
      ...status,
      call_id: '',
      lead_id: '',
      phone_number: '',
      vendor_lead_code: '',
      real_time_sub_status: subStatusValue || 'DEAD',
    }
  }

  const hasCallMarkers =
    hasMeaningfulVicidialValue(status.call_id) ||
    hasMeaningfulVicidialValue(status.lead_id) ||
    subStatusValue.includes('INCALL') ||
    subStatusValue.includes('LIVE') ||
    subStatusValue.includes('RING')

  if (hasCallMarkers) {
    return status
  }

  if (!['PAUSED', 'READY', 'CLOSER', 'LOGOUT', ''].includes(statusValue)) {
    return status
  }

  return {
    ...status,
    call_id: '',
    lead_id: '',
    phone_number: '',
    vendor_lead_code: '',
    real_time_sub_status: '',
  }
}

const hasVicidialCallContext = (session: Partial<VicidialSessionState> | null | undefined) => {
  const statusValue = String(session?.status || '')
    .trim()
    .toUpperCase()
  const subStatusValue = String(session?.realTimeSubStatus || '')
    .trim()
    .toUpperCase()
  if (isVicidialDispositionPendingState(statusValue, subStatusValue)) {
    return false
  }
  return (
    hasMeaningfulVicidialValue(session?.callId) ||
    hasMeaningfulVicidialValue(session?.leadId) ||
    hasMeaningfulVicidialValue(session?.phoneNumber) ||
    hasMeaningfulVicidialValue(session?.vendorLeadCode) ||
    ['INCALL', 'LIVE', 'QUEUE', 'RING'].some((marker) => statusValue.includes(marker)) ||
    ['INCALL', 'LIVE', 'QUEUE', 'RING'].some((marker) => subStatusValue.includes(marker))
  )
}

const hasConfirmedVicidialLiveCall = (
  session: Partial<VicidialSessionState> | null | undefined
) => {
  const statusValue = String(session?.status || '')
    .trim()
    .toUpperCase()
  const subStatusValue = String(session?.realTimeSubStatus || '')
    .trim()
    .toUpperCase()
  if (isVicidialDispositionPendingState(statusValue, subStatusValue)) {
    return false
  }
  return (
    hasMeaningfulVicidialValue(session?.callId) ||
    ['INCALL', 'LIVE', 'RING'].some((marker) => statusValue.includes(marker)) ||
    ['INCALL', 'LIVE', 'RING'].some((marker) => subStatusValue.includes(marker))
  )
}

const isVicidialActiveConversationState = (
  status?: string | null,
  realTimeSubStatus?: string | null
) => {
  const statusValue = String(status || '')
    .trim()
    .toUpperCase()
  const subStatusValue = String(realTimeSubStatus || '')
    .trim()
    .toUpperCase()
  if (isVicidialDispositionPendingState(statusValue, subStatusValue)) {
    return false
  }
  return ['INCALL', 'LIVE'].some(
    (marker) => statusValue.includes(marker) || subStatusValue.includes(marker)
  )
}

const createVicidialDispositionDetail = (
  session: Partial<VicidialSessionState> | null | undefined
): TelephonyVicidialDispositionDetail | null => {
  if (!session) return null

  const callId = String(session.callId || '').trim()
  const leadId = String(session.leadId || '').trim()
  const phoneNumber = String(session.phoneNumber || '').trim()
  const vendorLeadCode = String(session.vendorLeadCode || '').trim()
  const firstName = String(session.firstName || '').trim()
  const lastName = String(session.lastName || '').trim()
  const email = String(session.email || '').trim()
  const sessionId = String(session.sessionId || '').trim()
  const campaignId = String(session.campaignId || '').trim()
  const checkedAt = session.checkedAt || null
  const key = [
    sessionId,
    callId || leadId || vendorLeadCode || phoneNumber,
    campaignId,
    String(checkedAt || ''),
  ].join('|')

  if (!key.replace(/\|/g, '').trim()) {
    return null
  }

  return {
    key,
    status: String(session.status || '')
      .trim()
      .toUpperCase(),
    realTimeSubStatus: String(session.realTimeSubStatus || '')
      .trim()
      .toUpperCase(),
    callId,
    leadId,
    campaignId,
    phoneNumber,
    vendorLeadCode,
    firstName,
    lastName,
    email,
    sessionId,
    checkedAt,
  }
}

const createVicidialDispositionDetailFromStatus = (
  status: TelephonyStatusResponse | null | undefined,
  fallback?: TelephonyVicidialDispositionDetail | null
): TelephonyVicidialDispositionDetail | null => {
  if (!status && !fallback) return null

  const callId = String(status?.call_id || fallback?.callId || '').trim()
  const leadId = String(status?.lead_id || fallback?.leadId || '').trim()
  const phoneNumber = String(status?.phone_number || fallback?.phoneNumber || '').trim()
  const vendorLeadCode = String(status?.vendor_lead_code || fallback?.vendorLeadCode || '').trim()
  const firstName = String(status?.first_name || fallback?.firstName || '').trim()
  const lastName = String(status?.last_name || fallback?.lastName || '').trim()
  const email = String(status?.email || fallback?.email || '').trim()
  const sessionId = String(status?.session_id || fallback?.sessionId || '').trim()
  const campaignId = String(status?.campaign_id || fallback?.campaignId || '').trim()
  const stableSuffix = String(fallback?.checkedAt || 'DISPO').trim()
  const key = [
    sessionId,
    callId || leadId || vendorLeadCode || phoneNumber,
    campaignId,
    stableSuffix,
  ].join('|')

  if (!key.replace(/\|/g, '').trim()) {
    return null
  }

  return {
    key,
    status: String(status?.status || fallback?.status || '')
      .trim()
      .toUpperCase(),
    realTimeSubStatus: String(status?.real_time_sub_status || fallback?.realTimeSubStatus || '')
      .trim()
      .toUpperCase(),
    callId,
    leadId,
    campaignId,
    phoneNumber,
    vendorLeadCode,
    firstName,
    lastName,
    email,
    sessionId,
    checkedAt: Date.now(),
  }
}

const shouldUseExternalSecureWebphone = (targetUrl?: string, baseUrl?: string) => {
  if (typeof window === 'undefined') return false
  if (window.location.protocol === 'https:') return false

  const candidate = String(targetUrl || baseUrl || '').trim()
  return candidate.startsWith('https://')
}

const normalizeVicidialCampaignId = (value?: string | null) => String(value || '').trim()
const getStoredVicidialInboundGroups = () => {
  if (typeof window === 'undefined') return [] as string[]

  try {
    const raw = localStorage.getItem(STORAGE_NAMES.VICIDIAL_SELECTED_INGROUPS)
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed)
      ? parsed.map((value) => String(value || '').trim()).filter(Boolean)
      : []
  } catch (error) {
    return []
  }
}

const getStoredVicidialCampaignId = () =>
  typeof window === 'undefined'
    ? ''
    : normalizeVicidialCampaignId(localStorage.getItem(STORAGE_NAMES.VICIDIAL_SELECTED_CAMPAIGN))
const vicidialSessionMatchesCampaign = (
  status: Pick<TelephonyStatusResponse, 'campaign_id'> | null | undefined,
  expectedCampaignId?: string | null
) => {
  const normalizedExpected = normalizeVicidialCampaignId(expectedCampaignId)
  if (!normalizedExpected) return true
  return normalizeVicidialCampaignId(status?.campaign_id) === normalizedExpected
}

const createVicidialSessionState = (
  config: TelephonyResolveResponse,
  overrides?: Partial<VicidialSessionState>
): VicidialSessionState => ({
  connected: false,
  connecting: false,
  activeSession: false,
  agentUser: config.vicidial_agent_user || '',
  phoneLogin: config.vicidial_phone_login || '',
  selectedCampaignId:
    getStoredVicidialCampaignId() || normalizeVicidialCampaignId(config.vicidial_campaign_id),
  status: '',
  callId: '',
  localCallId: null,
  leadId: '',
  campaignId: '',
  pauseCode: '',
  realTimeSubStatus: '',
  sessionId: '',
  phoneNumber: '',
  vendorLeadCode: '',
  firstName: '',
  lastName: '',
  email: '',
  comments: '',
  callsInQueueCount: 0,
  callStartedAt: null,
  wrapUpStartedAt: null,
  message: config.vicidial_enabled
    ? 'PolicyDial is not connected in this browser.'
    : 'PolicyDial is not enabled for this account.',
  rawResponse: '',
  checkedAt: null,
  ...overrides,
})

const useTelephonyController = () => {
  const {auth} = useAuth()
  const [config, setConfig] = useState<TelephonyResolveResponse>(DEFAULT_TELEPHONY_CONFIG)
  const [loading, setLoading] = useState(false)
  const [resolved, setResolved] = useState(false)
  const [vicidialCampaignOptions, setVicidialCampaignOptions] = useState<TelephonyCampaignOption[]>(
    []
  )
  const [loadingVicidialCampaigns, setLoadingVicidialCampaigns] = useState(false)
  const [vicidialInboundGroupOptions, setVicidialInboundGroupOptions] = useState<
    TelephonyInboundGroupOption[]
  >([])
  const [loadingVicidialInboundGroups, setLoadingVicidialInboundGroups] = useState(false)
  const [selectedVicidialInboundGroups, setSelectedVicidialInboundGroups] = useState<string[]>(
    getStoredVicidialInboundGroups
  )
  const lastVicidialLiveCallRef = useRef<TelephonyVicidialDispositionDetail | null>(null)
  const lastConfirmedVicidialLiveCallRef = useRef<TelephonyVicidialDispositionDetail | null>(null)
  const lastVicidialDispositionKeyRef = useRef('')
  const [vicidialSession, setVicidialSession] = useState<VicidialSessionState>(() =>
    createVicidialSessionState(DEFAULT_TELEPHONY_CONFIG, {
      connected:
        Boolean(localStorage.getItem(STORAGE_NAMES.VICIDIAL_SESSION)) &&
        !isVicidialForceDisconnected(),
      selectedCampaignId: normalizeVicidialCampaignId(
        localStorage.getItem(STORAGE_NAMES.VICIDIAL_SELECTED_CAMPAIGN)
      ),
    })
  )

  const getToken = useCallback(() => {
    return auth?.api_token || localStorage.getItem(STORAGE_NAMES.TOKEN)
  }, [auth?.api_token])

  useEffect(() => {
    const liveCallDetail = createVicidialDispositionDetail(vicidialSession)
    const dispositionPending = isVicidialDispositionPendingState(
      vicidialSession.status,
      vicidialSession.realTimeSubStatus
    )
    const hasLiveCall = hasVicidialCallContext(vicidialSession)
    const hasConfirmedLiveCall = hasConfirmedVicidialLiveCall(vicidialSession)

    if (hasLiveCall && liveCallDetail) {
      lastVicidialLiveCallRef.current = liveCallDetail
      if (hasConfirmedLiveCall) {
        lastConfirmedVicidialLiveCallRef.current = liveCallDetail
      }
      return
    }

    if (!hasLiveCall && !dispositionPending) {
      lastConfirmedVicidialLiveCallRef.current = null
    }

    // Show the disposition prompt when VICIdial enters DISPO/DEAD OR when a call the
    // agent was just on has ended. In the PolicyTools/MeetMe flow a customer hangup
    // leaves the agent at PAUSED (the native DISPO state never fires), so without the
    // call-ended path the disposition modal would never appear and calls stay NODISP.
    if (hasLiveCall || !lastVicidialLiveCallRef.current) {
      return
    }

    const completedCall = {
      ...lastVicidialLiveCallRef.current,
      status: String(vicidialSession.status || '')
        .trim()
        .toUpperCase(),
      realTimeSubStatus: String(vicidialSession.realTimeSubStatus || '')
        .trim()
        .toUpperCase(),
      checkedAt: vicidialSession.checkedAt || Date.now(),
    }

    if (!completedCall.key || completedCall.key === lastVicidialDispositionKeyRef.current) {
      return
    }

    lastVicidialLiveCallRef.current = null
    lastVicidialDispositionKeyRef.current = completedCall.key
    dispatchVicidialDisposition(completedCall)
  }, [
    vicidialSession.callId,
    vicidialSession.campaignId,
    vicidialSession.checkedAt,
    vicidialSession.leadId,
    vicidialSession.phoneNumber,
    vicidialSession.realTimeSubStatus,
    vicidialSession.sessionId,
    vicidialSession.status,
    vicidialSession.vendorLeadCode,
  ])

  const getEffectiveVicidialCampaignId = useCallback(() => {
    return normalizeVicidialCampaignId(
      vicidialSession.selectedCampaignId || config.vicidial_campaign_id || ''
    )
  }, [config.vicidial_campaign_id, vicidialSession.selectedCampaignId])

  const setVicidialCampaign = useCallback((campaignId: string) => {
    const normalizedCampaignId = normalizeVicidialCampaignId(campaignId)
    if (normalizedCampaignId) {
      localStorage.setItem(STORAGE_NAMES.VICIDIAL_SELECTED_CAMPAIGN, normalizedCampaignId)
    } else {
      localStorage.removeItem(STORAGE_NAMES.VICIDIAL_SELECTED_CAMPAIGN)
    }

    setVicidialSession((current) => ({
      ...current,
      selectedCampaignId: normalizedCampaignId,
    }))
  }, [])

  const setVicidialInboundGroups = useCallback((ingroups: string[]) => {
    const normalizedGroups = (ingroups || [])
      .map((value) => String(value || '').trim())
      .filter(Boolean)
    localStorage.setItem(STORAGE_NAMES.VICIDIAL_SELECTED_INGROUPS, JSON.stringify(normalizedGroups))
    setSelectedVicidialInboundGroups(normalizedGroups)
  }, [])

  const loadVicidialCampaigns = useCallback(async () => {
    const token = getToken()
    if (!token) return []

    setLoadingVicidialCampaigns(true)
    try {
      const response = await axios.post<{campaigns?: TelephonyCampaignOption[]}>(
        `${URL}/api/telephony/vicidial/campaigns`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      )

      const nextCampaigns = (response.data?.campaigns || []) as TelephonyCampaignOption[]
      setVicidialCampaignOptions(nextCampaigns)
      return nextCampaigns
    } catch (error: any) {
      const message = error?.response?.data?.error || 'Unable to load PolicyDial campaigns.'
      toast.error(message)
      setVicidialCampaignOptions([])
      return []
    } finally {
      setLoadingVicidialCampaigns(false)
    }
  }, [getToken])

  const loadVicidialInboundGroups = useCallback(async () => {
    const token = getToken()
    if (!token) return [] as TelephonyInboundGroupOption[]

    setLoadingVicidialInboundGroups(true)
    try {
      const response = await axios.get<{ingroups?: TelephonyInboundGroupOption[]}>(
        `${URL}/api/telephony/vicidial/ingroups?provider=VICIDIAL`,
        {
          headers: {Authorization: `Bearer ${token}`},
        }
      )

      const nextGroups = (response.data?.ingroups || []) as TelephonyInboundGroupOption[]
      setVicidialInboundGroupOptions(nextGroups)
      return nextGroups
    } catch (error: any) {
      const message = error?.response?.data?.error || 'Unable to load PolicyDial inbound groups.'
      toast.error(message)
      setVicidialInboundGroupOptions([])
      return []
    } finally {
      setLoadingVicidialInboundGroups(false)
    }
  }, [getToken])

  const resolveConfig = useCallback(async () => {
    const token = getToken()
    if (!token) {
      setResolved(true)
      return DEFAULT_TELEPHONY_CONFIG
    }

    setLoading(true)
    try {
      const response = await axios.get<TelephonyResolveResponse>(`${URL}/api/telephony/resolve`, {
        headers: {Authorization: `Bearer ${token}`},
      })
      setConfig(response.data)
      return response.data
    } catch (error) {
      console.error('Error resolving telephony config', error)
      return DEFAULT_TELEPHONY_CONFIG
    } finally {
      setLoading(false)
      setResolved(true)
    }
  }, [getToken])

  useEffect(() => {
    resolveConfig()
  }, [resolveConfig])

  useEffect(() => {
    const storedCampaignId = normalizeVicidialCampaignId(
      localStorage.getItem(STORAGE_NAMES.VICIDIAL_SELECTED_CAMPAIGN)
    )
    const resolvedCampaignId = normalizeVicidialCampaignId(config.vicidial_campaign_id)
    const nextSelectedCampaignId = storedCampaignId || resolvedCampaignId

    setVicidialSession((current) =>
      createVicidialSessionState(config, {
        ...current,
        agentUser: config.vicidial_agent_user || '',
        phoneLogin: config.vicidial_phone_login || '',
        selectedCampaignId: nextSelectedCampaignId || current.selectedCampaignId || '',
        connected:
          (current.connected || Boolean(localStorage.getItem(STORAGE_NAMES.VICIDIAL_SESSION))) &&
          !isVicidialForceDisconnected(),
      })
    )
  }, [config])

  const openWebphone = useCallback(
    async (providerOverride?: TelephonyProviderType, options?: OpenWebphoneOptions) => {
      const token = getToken()
      if (!token) return null

      const activeProvider = providerOverride || config.provider

      if (activeProvider !== 'VICIDIAL') {
        toast.info('The current telephony provider does not use an embedded webphone.')
        return null
      }

      localStorage.removeItem(STORAGE_NAMES.VICIDIAL_FORCE_DISCONNECTED)

      const pendingExternalWindow =
        options?.allowExternalFallback !== false &&
        !options?.preferAgentSession &&
        shouldUseExternalSecureWebphone(undefined, config.vicidial_base_url)
          ? window.open('', 'PolicyToolsVicidialWebphone')
          : null

      try {
        const params = new URLSearchParams()
        if (providerOverride) {
          params.set('provider', providerOverride)
        }
        if (options?.preferAgentSession) {
          params.set('session', 'agent')
        }
        if (options?.phoneOnly) {
          params.set('phone_only', '1')
        }
        const effectiveCampaignId = getEffectiveVicidialCampaignId()
        if (effectiveCampaignId) {
          params.set('campaign_id', effectiveCampaignId)
        }
        const query = params.toString() ? `?${params.toString()}` : ''
        const response = await axios.get<TelephonyWebphoneResponse>(
          `${URL}/api/telephony/webphone-url${query}`,
          {
            headers: {Authorization: `Bearer ${token}`},
          }
        )

        if (window.location.protocol === 'https:' && response.data.url?.startsWith('http://')) {
          response.data.url = response.data.url.replace('http://', 'https://')
        }

        if (!response.data.url) {
          if (pendingExternalWindow && !pendingExternalWindow.closed) {
            pendingExternalWindow.close()
          }
          toast.error('PolicyDial did not return a webphone URL.')
          return null
        }

        if (
          shouldUseExternalSecureWebphone(response.data.url, config.vicidial_base_url) &&
          pendingExternalWindow &&
          !pendingExternalWindow.closed
        ) {
          ;(window as any)[VICIDIAL_EXTERNAL_WINDOW_KEY] = pendingExternalWindow
          pendingExternalWindow.location.replace(response.data.url)
          pendingExternalWindow.focus?.()
          toast.info('PolicyDial opened in a secure tab because the app is running over HTTP.')
          return {
            ...response.data,
            opened_externally: true,
          }
        }

        dispatchTelephonyDrawer({
          url: response.data.url,
          provider: 'VICIDIAL',
          title: 'PolicyDial Webphone',
          warning: response.data.warning,
          width: response.data.display_mode === 'agent' ? 'min(88vw, 1320px)' : '460px',
          displayMode: response.data.display_mode,
          diagnostics: {
            agent_user: config.vicidial_agent_user,
            message:
              response.data.source === 'autologin'
                ? 'Signing into PolicyDial...'
                : 'Opening PolicyDial webphone...',
          },
        })

        if (response.data.warning && response.data.source === 'api') {
          toast.info(response.data.warning)
        }

        return response.data
      } catch (error: any) {
        if (pendingExternalWindow && !pendingExternalWindow.closed) {
          pendingExternalWindow.close()
        }
        const message = error?.response?.data?.error || 'Unable to open the PolicyDial webphone.'
        toast.error(message)
        return null
      }
    },
    [config.provider, config.vicidial_base_url, getEffectiveVicidialCampaignId, getToken]
  )

  const openDirectVicidialWebphone = useCallback(async () => {
    return openWebphone('VICIDIAL', {
      preferAgentSession: false,
    })
  }, [openWebphone])

  const getAgentStatus = useCallback(
    async (providerOverride?: TelephonyProviderType, options?: GetAgentStatusOptions) => {
      const token = getToken()
      if (!token) return null

      try {
        const queryParams: string[] = []
        if (providerOverride) queryParams.push(`provider=${encodeURIComponent(providerOverride)}`)
        const groups = (selectedVicidialInboundGroups || []).filter(Boolean)
        if (groups.length) {
          queryParams.push(`in_groups=${encodeURIComponent(groups.join(','))}`)
        }
        const query = queryParams.length ? `?${queryParams.join('&')}` : ''
        const response = await axios.get<TelephonyStatusResponse>(
          `${URL}/api/telephony/status${query}`,
          {
            headers: {Authorization: `Bearer ${token}`},
          }
        )
        return response.data
      } catch (error: any) {
        const message =
          error?.response?.data?.error || 'Unable to read the PolicyDial agent status.'
        if (!options?.silent) {
          toast.error(message)
        }
        return {
          provider: providerOverride || config.provider,
          agent_user: config.vicidial_agent_user || '',
          active_session: false,
          raw_response: message,
          status: '',
          call_id: '',
          lead_id: '',
          campaign_id: '',
          pause_code: '',
          real_time_sub_status: '',
          phone_number: '',
          vendor_lead_code: '',
          session_id: '',
        } as TelephonyStatusResponse
      }
    },
    [config.provider, config.vicidial_agent_user, getToken, selectedVicidialInboundGroups]
  )

  const requestVicidialAgentConnect = useCallback(
    async (options?: {silent?: boolean}) => {
      const token = getToken()
      if (!token) return null

      try {
        const effectiveCampaignId = getEffectiveVicidialCampaignId()
        const response = await axios.post<TelephonyConnectAgentResponse>(
          `${URL}/api/telephony/connect-agent`,
          {
            provider: 'VICIDIAL',
            ...(effectiveCampaignId ? {campaign_id: effectiveCampaignId} : {}),
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        )
        return response.data
      } catch (error: any) {
        if (!options?.silent) {
          const message =
            error?.response?.data?.error || 'Unable to request the PolicyDial agent callback.'
          toast.error(message)
        }
        return null
      }
    },
    [getEffectiveVicidialCampaignId, getToken]
  )

  const syncVicidialSessionState = useCallback(
    (
      status: TelephonyStatusResponse | null,
      overrides?: Partial<VicidialSessionState>,
      persistConnected = Boolean(status?.active_session)
    ) => {
      if (persistConnected) {
        const wasConnected = Boolean(localStorage.getItem(STORAGE_NAMES.VICIDIAL_SESSION))
        localStorage.setItem(STORAGE_NAMES.VICIDIAL_SESSION, 'connected')
        if (!wasConnected) {
          window.dispatchEvent(new CustomEvent('TELEPHONY_VICIDIAL_LOGIN_EVENT'))
        }
      } else if (!overrides?.connecting) {
        localStorage.removeItem(STORAGE_NAMES.VICIDIAL_SESSION)
      }

      const nextStatusValue = status?.status || ''
      const nextSubStatusValue = status?.real_time_sub_status || ''
      const nextDispoPending = isVicidialDispositionPendingState(
        nextStatusValue,
        nextSubStatusValue
      )

      setVicidialSession((current) => {
        const prevDispoPending = isVicidialDispositionPendingState(
          current.status,
          current.realTimeSubStatus
        )
        // Treat any non-disposition state with an active call_id as a live
        // conversation, matching the drawer's effective-status logic that
        // displays "INCALL" for raw QUEUE + call_id. Without this, the call
        // timer never starts on inbound / hopper calls.
        const prevActiveConversation =
          !prevDispoPending &&
          (isVicidialActiveConversationState(current.status, current.realTimeSubStatus) ||
            hasMeaningfulVicidialValue(current.callId))
        const nextActiveConversation =
          !nextDispoPending &&
          (isVicidialActiveConversationState(nextStatusValue, nextSubStatusValue) ||
            hasMeaningfulVicidialValue(status?.call_id))
        const callStartedAt = nextActiveConversation
          ? prevActiveConversation && current.callStartedAt
            ? current.callStartedAt
            : Date.now()
          : null
        const wrapUpStartedAt = nextDispoPending
          ? prevDispoPending && current.wrapUpStartedAt
            ? current.wrapUpStartedAt
            : Date.now()
          : null
        const nextHasCallContext =
          Boolean(status?.policytools_call_completed) === false &&
          (hasMeaningfulVicidialValue(status?.call_id) ||
            hasMeaningfulVicidialValue(status?.lead_id) ||
            hasMeaningfulVicidialValue(status?.phone_number) ||
            hasMeaningfulVicidialValue(status?.vendor_lead_code) ||
            ['INCALL', 'LIVE', 'QUEUE', 'RING'].some(
              (marker) =>
                nextStatusValue.toUpperCase().includes(marker) ||
                nextSubStatusValue.toUpperCase().includes(marker)
            ))
        const hasLocalCallIdOverride = Boolean(
          overrides && Object.prototype.hasOwnProperty.call(overrides, 'localCallId')
        )
        const nextLocalCallId = hasLocalCallIdOverride
          ? overrides?.localCallId ?? null
          : typeof status?.policytools_call_id === 'number'
          ? status.policytools_call_id
          : nextHasCallContext
          ? current.localCallId
          : null

        const nextState = createVicidialSessionState(config, {
          connected: persistConnected,
          connecting: false,
          activeSession: Boolean(status?.active_session),
          agentUser: status?.agent_user || config.vicidial_agent_user || '',
          phoneLogin: config.vicidial_phone_login || '',
          status: nextStatusValue,
          callId: status?.call_id || '',
          localCallId: nextLocalCallId,
          leadId: status?.lead_id || '',
          campaignId: status?.campaign_id || '',
          pauseCode: status?.pause_code || '',
          realTimeSubStatus: nextSubStatusValue,
          sessionId: status?.session_id || '',
          phoneNumber: status?.phone_number || '',
          vendorLeadCode: status?.vendor_lead_code || '',
          firstName: status?.first_name || '',
          lastName: status?.last_name || '',
          email: status?.email || '',
          comments: status?.comments || '',
          callsInQueueCount: Math.max(0, Number(status?.calls_in_queue_count) || 0),
          callStartedAt,
          wrapUpStartedAt,
          rawResponse: status?.raw_response || '',
          checkedAt: Date.now(),
          message: persistConnected
            ? 'PolicyDial is connected and ready for calls.'
            : isVicidialLoggedOutStatus(status)
            ? VICIDIAL_LOGGED_OUT_MESSAGE
            : status?.raw_response || 'PolicyDial is not connected in this browser.',
          ...overrides,
        })

        dispatchVicidialSessionState(nextState)
        return nextState
      })
    },
    [config]
  )

  const syncVicidialDrawerDiagnostics = useCallback(
    (status: TelephonyStatusResponse | null, message?: string, fallbackRaw?: string) => {
      dispatchTelephonyDrawerDiagnostics({
        message,
        agent_user: status?.agent_user || config.vicidial_agent_user,
        status: status?.status || '',
        call_id: status?.call_id || '',
        campaign_id: status?.campaign_id || '',
        pause_code: status?.pause_code || '',
        session_id: status?.session_id || '',
        phone_number: status?.phone_number || '',
        vendor_lead_code: status?.vendor_lead_code || '',
        real_time_sub_status: status?.real_time_sub_status || '',
        raw_response: status?.raw_response || fallbackRaw || '',
      })
    },
    [config.vicidial_agent_user]
  )

  const refreshVicidialSession = useCallback(
    async (options?: GetAgentStatusOptions) => {
      if (!config.vicidial_enabled || !config.vicidial_agent_user) {
        localStorage.removeItem(STORAGE_NAMES.VICIDIAL_SESSION)
        setVicidialSession(createVicidialSessionState(config))
        return null
      }

      if (isVicidialForceDisconnected()) {
        localStorage.removeItem(STORAGE_NAMES.VICIDIAL_SESSION)
        setVicidialSession(
          createVicidialSessionState(config, {
            connected: false,
            connecting: false,
            activeSession: false,
            status: 'LOGOUT',
            message: VICIDIAL_LOGGED_OUT_MESSAGE,
            rawResponse: '',
            checkedAt: Date.now(),
          })
        )
        return {
          provider: 'VICIDIAL',
          agent_user: config.vicidial_agent_user || '',
          active_session: false,
          raw_response: '',
          status: 'LOGOUT',
          call_id: '',
          lead_id: '',
          campaign_id: '',
          pause_code: '',
          real_time_sub_status: '',
          phone_number: '',
          vendor_lead_code: '',
          session_id: '',
        } as TelephonyStatusResponse
      }

      const preserveOpenState = vicidialSession.connected
      const status = await getAgentStatus('VICIDIAL', options)
      const normalizedStatus = clearVicidialCallContextIfIdle(status)
      const dispositionPending = isVicidialDispositionPendingState(
        normalizedStatus?.status,
        normalizedStatus?.real_time_sub_status
      )
      if (dispositionPending) {
        const completedCall = createVicidialDispositionDetailFromStatus(
          normalizedStatus,
          lastVicidialLiveCallRef.current
        )
        if (completedCall?.key && completedCall.key !== lastVicidialDispositionKeyRef.current) {
          lastVicidialLiveCallRef.current = null
          lastVicidialDispositionKeyRef.current = completedCall.key
          dispatchVicidialDisposition(completedCall)
        }
      }
      if (!normalizedStatus) {
        syncVicidialSessionState(
          null,
          {
            connected: preserveOpenState,
            message: 'Unable to verify the PolicyDial session right now.',
            checkedAt: Date.now(),
          },
          false
        )
        return null
      }

      if (preserveOpenState && isVicidialStatusConnectivityError(normalizedStatus.raw_response)) {
        syncVicidialSessionState(
          {
            ...normalizedStatus,
            status: 'UNVERIFIED',
            active_session: false,
            pause_code: '',
          },
          {
            connected: true,
            activeSession: false,
            status: 'UNVERIFIED',
            message: VICIDIAL_UNVERIFIED_MESSAGE,
          },
          false
        )
        return normalizedStatus
      }

      if (isVicidialLoggedOutStatus(normalizedStatus)) {
        syncVicidialSessionState(
          {
            ...normalizedStatus,
            active_session: false,
            raw_response: '',
            pause_code: '',
          },
          {
            connected: false,
            activeSession: false,
            status: 'LOGOUT',
            message: VICIDIAL_LOGGED_OUT_MESSAGE,
            rawResponse: '',
          },
          false
        )
        return normalizedStatus
      }

      if (!normalizedStatus.active_session && preserveOpenState) {
        syncVicidialSessionState(
          normalizedStatus,
          {
            connected: true,
            message: normalizedStatus.raw_response || VICIDIAL_WAITING_CONFIRMATION_MESSAGE,
          },
          false
        )
        syncVicidialDrawerDiagnostics(
          normalizedStatus,
          normalizedStatus.raw_response || VICIDIAL_WAITING_CONFIRMATION_MESSAGE
        )
        return normalizedStatus
      }

      syncVicidialSessionState(normalizedStatus)
      syncVicidialDrawerDiagnostics(normalizedStatus, normalizedStatus.raw_response || undefined)
      return normalizedStatus
    },
    [
      config,
      getAgentStatus,
      syncVicidialDrawerDiagnostics,
      syncVicidialSessionState,
      vicidialSession.connected,
    ]
  )

  const connectVicidialSession = useCallback(async () => {
    if (!config.vicidial_enabled) {
      toast.error('PolicyDial is not enabled for this account.')
      return null
    }

    localStorage.removeItem(STORAGE_NAMES.VICIDIAL_FORCE_DISCONNECTED)

    if (!config.vicidial_agent_user) {
      toast.error('No PolicyDial agent user is configured for this account.')
      return null
    }

    setVicidialSession(
      createVicidialSessionState(config, {
        connecting: true,
        message: 'Preparing PolicyDial session...',
      })
    )

    try {
      const token = getToken()
      if (token) {
        await axios.post(
          `${URL}/api/telephony/disconnect-agent`,
          {provider: 'VICIDIAL'},
          {headers: {Authorization: `Bearer ${token}`, 'Content-Type': 'application/json'}}
        )
        localStorage.removeItem(STORAGE_NAMES.VICIDIAL_SESSION)
      }
    } catch (_ignored) {}

    setVicidialSession(
      createVicidialSessionState(config, {
        connecting: true,
        message: 'Opening PolicyDial and waiting for the agent session to register...',
      })
    )

    const webphoneResponse = await openWebphone('VICIDIAL', {
      preferAgentSession: true,
      allowExternalFallback: false,
    })

    if (!webphoneResponse) {
      setVicidialSession(
        createVicidialSessionState(config, {
          message: 'Unable to open the PolicyDial webphone.',
        })
      )
      return null
    }

    const waitingMessage =
      webphoneResponse.source === 'autologin'
        ? 'Signing into PolicyDial and registering the agent webphone...'
        : 'Opening the PolicyDial webphone and checking the agent session...'
    const shouldSkipFollowUpWebphoneOpen =
      Boolean(webphoneResponse.opened_externally) || webphoneResponse.source === 'autologin'
    const effectiveCampaignId = getEffectiveVicidialCampaignId()

    setVicidialSession(
      createVicidialSessionState(config, {
        connected: true,
        connecting: true,
        message: waitingMessage,
      })
    )
    dispatchTelephonyDrawerDiagnostics({
      message: waitingMessage,
      agent_user: config.vicidial_agent_user,
    })

    let callbackRequested = false
    for (let attempt = 0; attempt < 12; attempt += 1) {
      const status = await getAgentStatus('VICIDIAL', {silent: true})
      const campaignReady = vicidialSessionMatchesCampaign(status, effectiveCampaignId)
      if (status?.active_session && campaignReady) {
        syncVicidialSessionState(status, {
          message: 'PolicyDial is connected and ready for calls.',
        })
        if (!shouldSkipFollowUpWebphoneOpen) {
          await openDirectVicidialWebphone()
        }
        toast.success('PolicyDial connected and ready for calls.')
        return status
      }

      if (status) {
        if (isVicidialStatusConnectivityError(status.raw_response)) {
          syncVicidialSessionState(
            {
              ...status,
              status: 'UNVERIFIED',
              active_session: false,
            },
            {
              connected: true,
              connecting: false,
              activeSession: false,
              status: 'UNVERIFIED',
              message: VICIDIAL_UNVERIFIED_MESSAGE,
            },
            false
          )
          dispatchTelephonyDrawerDiagnostics({
            message: VICIDIAL_UNVERIFIED_MESSAGE,
            agent_user: config.vicidial_agent_user,
            status: 'UNVERIFIED',
            raw_response: status.raw_response || '',
          })
          toast.info(
            'PolicyDial opened in the browser, but backend verification is unavailable in local development.'
          )
          return status
        }

        syncVicidialSessionState(
          status,
          {
            connected: true,
            connecting: true,
            message:
              !campaignReady && effectiveCampaignId
                ? `Switching PolicyDial to campaign ${effectiveCampaignId}...`
                : status.raw_response || VICIDIAL_WAITING_CONFIRMATION_MESSAGE,
          },
          false
        )
      }

      if (!callbackRequested && attempt >= 2) {
        callbackRequested = true
        setVicidialSession(
          createVicidialSessionState(config, {
            connected: true,
            connecting: true,
            message: 'Requesting the PolicyDial agent callback...',
          })
        )
        dispatchTelephonyDrawerDiagnostics({
          message: 'Requesting the PolicyDial agent callback...',
          agent_user: config.vicidial_agent_user,
          raw_response: status?.raw_response || '',
        })
        const connectResponse = await requestVicidialAgentConnect({silent: true})
        if (connectResponse?.active_session) {
          const connectedStatus: TelephonyStatusResponse = {
            provider: 'VICIDIAL',
            agent_user: connectResponse.agent_user || config.vicidial_agent_user,
            active_session: true,
            raw_response: connectResponse.raw_response || '',
            status: connectResponse.status || '',
            call_id: '',
            lead_id: '',
            campaign_id: connectResponse.campaign_id || '',
            pause_code: '',
            real_time_sub_status: '',
            phone_number: connectResponse.phone_number || '',
            vendor_lead_code: '',
            session_id: connectResponse.session_id || '',
          }
          syncVicidialSessionState(connectedStatus, {
            message: 'PolicyDial is connected and ready for calls.',
          })
          if (!shouldSkipFollowUpWebphoneOpen) {
            await openDirectVicidialWebphone()
          }
          toast.success('PolicyDial connected and ready for calls.')
          return connectedStatus
        }
      }

      await delay(1500)
    }

    const finalStatus = await getAgentStatus('VICIDIAL', {silent: true})
    if (
      finalStatus?.active_session &&
      vicidialSessionMatchesCampaign(finalStatus, effectiveCampaignId)
    ) {
      syncVicidialSessionState(finalStatus, {
        message: 'PolicyDial is connected and ready for calls.',
      })
      if (!shouldSkipFollowUpWebphoneOpen) {
        await openDirectVicidialWebphone()
      }
      toast.success('PolicyDial connected and ready for calls.')
      return finalStatus
    }

    syncVicidialSessionState(
      finalStatus,
      {
        connected: true,
        connecting: false,
        message:
          !vicidialSessionMatchesCampaign(finalStatus, effectiveCampaignId) && effectiveCampaignId
            ? `PolicyDial is still on a different campaign. Switch it to ${effectiveCampaignId} and try again.`
            : finalStatus?.raw_response || VICIDIAL_WAITING_CONFIRMATION_MESSAGE,
      },
      false
    )
    toast.info('Finish the PolicyDial login in the panel, then try Connect again.')
    return finalStatus
  }, [
    config,
    getAgentStatus,
    getEffectiveVicidialCampaignId,
    openDirectVicidialWebphone,
    openWebphone,
    requestVicidialAgentConnect,
    syncVicidialSessionState,
  ])

  const disconnectVicidialSession = useCallback(async () => {
    const token = getToken()

    if (!token) {
      localStorage.removeItem(STORAGE_NAMES.VICIDIAL_SESSION)
      dispatchTelephonyDrawerClose()
      setVicidialSession(
        createVicidialSessionState(config, {
          message: 'PolicyDial session removed from this browser.',
        })
      )
      return
    }

    setVicidialSession((current) => ({
      ...current,
      connecting: true,
      message: 'Logging out of PolicyDial...',
    }))
    dispatchTelephonyDrawerDiagnostics({
      message: 'Logging out of PolicyDial...',
      agent_user: config.vicidial_agent_user,
      status: vicidialSession.status,
      campaign_id: vicidialSession.campaignId,
      session_id: vicidialSession.sessionId,
      phone_number: vicidialSession.phoneNumber,
      raw_response: vicidialSession.rawResponse,
    })

    try {
      const response = await axios.post<TelephonyDisconnectResponse>(
        `${URL}/api/telephony/disconnect-agent`,
        {provider: 'VICIDIAL'},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      )

      localStorage.removeItem(STORAGE_NAMES.VICIDIAL_SESSION)
      localStorage.setItem(STORAGE_NAMES.VICIDIAL_FORCE_DISCONNECTED, '1')
      dispatchTelephonyDrawerClose()
      setVicidialSession(
        createVicidialSessionState(config, {
          connected: false,
          connecting: false,
          activeSession: false,
          agentUser: response.data.agent_user || config.vicidial_agent_user || '',
          phoneLogin: config.vicidial_phone_login || '',
          status: response.data.active_session ? 'LOGOUT_PENDING' : 'LOGOUT',
          campaignId: '',
          sessionId: '',
          phoneNumber: '',
          rawResponse: response.data.raw_response || '',
          checkedAt: Date.now(),
          message: response.data.active_session
            ? 'PolicyDial logout was sent from PolicyTools. If the secure PolicyDial tab is still open, close it or use the native LOGOUT link there.'
            : 'PolicyDial logged out successfully.',
        })
      )
      window.dispatchEvent(new CustomEvent(TELEPHONY_VICIDIAL_LOGOUT_EVENT))

      const channel = new BroadcastChannel('policytools_vicidial_channel')
      channel.postMessage('CLOSE_WEBPHONE')
      channel.close()

      toast.info(
        response.data.active_session
          ? 'PolicyDial logout was sent. If another PolicyDial browser tab stays open, close it or use the native LOGOUT there.'
          : 'PolicyDial logged out.'
      )
      return response.data
    } catch (error: any) {
      const message = error?.response?.data?.error || 'Unable to log out from PolicyDial.'
      setVicidialSession((current) => ({
        ...current,
        connecting: false,
        message,
      }))
      dispatchTelephonyDrawerDiagnostics({
        message,
        agent_user: config.vicidial_agent_user,
        status: vicidialSession.status,
        campaign_id: vicidialSession.campaignId,
        session_id: vicidialSession.sessionId,
        phone_number: vicidialSession.phoneNumber,
        raw_response: vicidialSession.rawResponse,
      })
      toast.error(message)
      return null
    }
  }, [
    config,
    getToken,
    vicidialSession.campaignId,
    vicidialSession.phoneNumber,
    vicidialSession.rawResponse,
    vicidialSession.sessionId,
    vicidialSession.status,
  ])

  const recoverStuckVicidialSession = useCallback(async () => {
    const token = getToken()
    if (!token) return null
    try {
      const response = await axios.post<TelephonyVicidialActionResponse & {recovered?: boolean}>(
        `${URL}/api/telephony/vicidial/recover-stuck`,
        {provider: 'VICIDIAL'},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      )
      await refreshVicidialSession({silent: true})
      if (response.data?.recovered) {
        dispatchTelephonyDrawerDiagnostics({
          message: 'Recovered a stale PolicyDial queue state automatically.',
          agent_user: config.vicidial_agent_user,
          status: response.data.status,
          campaign_id: response.data.campaign_id,
          pause_code: response.data.pause_code,
          session_id: response.data.session_id,
          phone_number: vicidialSession.phoneNumber,
          raw_response: response.data.raw_response || vicidialSession.rawResponse,
        })
      }
      return response.data
    } catch (error: any) {
      const message =
        error?.response?.data?.error || 'Unable to recover the PolicyDial agent state.'
      toast.error(message)
      return null
    }
  }, [
    config.vicidial_agent_user,
    getToken,
    refreshVicidialSession,
    vicidialSession.phoneNumber,
    vicidialSession.rawResponse,
  ])

  const queueStartTimestampRef = useRef<number | null>(null)
  const queueFingerprintRef = useRef<string>('')
  const recoveryInFlightRef = useRef<boolean>(false)

  useEffect(() => {
    const statusValue = String(vicidialSession.status || '')
      .trim()
      .toUpperCase()
    const subStatus = String(vicidialSession.realTimeSubStatus || '')
      .trim()
      .toUpperCase()
    const leadId = String(vicidialSession.leadId || '').trim()
    const callId = String(vicidialSession.callId || '').trim()
    const lastConfirmedLiveCall = lastConfirmedVicidialLiveCallRef.current
    const queueFingerprint = [
      String(vicidialSession.sessionId || '').trim(),
      leadId,
      callId,
      subStatus,
    ].join('|')
    const matchesLastConfirmedCall =
      Boolean(lastConfirmedLiveCall) &&
      ((callId &&
        lastConfirmedLiveCall?.callId &&
        callId === String(lastConfirmedLiveCall.callId || '').trim()) ||
        (leadId &&
          lastConfirmedLiveCall?.leadId &&
          leadId === String(lastConfirmedLiveCall.leadId || '').trim() &&
          String(vicidialSession.sessionId || '').trim() ===
            String(lastConfirmedLiveCall.sessionId || '').trim()))
    const isOrphanableQueue =
      statusValue === 'QUEUE' &&
      !['INCALL', 'LIVE', 'RING'].some((m) => subStatus.includes(m)) &&
      Boolean(leadId) &&
      leadId !== '0' &&
      !hasMeaningfulVicidialValue(callId) &&
      matchesLastConfirmedCall

    if (!isOrphanableQueue) {
      queueStartTimestampRef.current = null
      queueFingerprintRef.current = ''
      return
    }

    if (
      queueStartTimestampRef.current === null ||
      queueFingerprintRef.current !== queueFingerprint
    ) {
      queueStartTimestampRef.current = Date.now()
      queueFingerprintRef.current = queueFingerprint
      return
    }

    const elapsedMs = Date.now() - queueStartTimestampRef.current
    if (elapsedMs > 8000 && !recoveryInFlightRef.current) {
      recoveryInFlightRef.current = true
      recoverStuckVicidialSession().finally(() => {
        recoveryInFlightRef.current = false
        queueStartTimestampRef.current = null
        queueFingerprintRef.current = ''
      })
    }
  }, [
    vicidialSession.callId,
    vicidialSession.checkedAt,
    vicidialSession.leadId,
    vicidialSession.realTimeSubStatus,
    vicidialSession.sessionId,
    vicidialSession.status,
    recoverStuckVicidialSession,
  ])

  const runVicidialAction = useCallback(
    async (
      endpoint: string,
      body: Record<string, unknown>,
      actionMessage: string,
      actionErrorMessage: string
    ) => {
      const token = getToken()
      if (!token) return null

      if (!config.vicidial_enabled || !config.vicidial_agent_user) {
        toast.error('PolicyDial is not configured for this account.')
        return null
      }

      dispatchTelephonyDrawerDiagnostics({
        message: actionMessage,
        agent_user: config.vicidial_agent_user,
        status: vicidialSession.status,
        campaign_id: vicidialSession.campaignId,
        pause_code: vicidialSession.pauseCode,
        session_id: vicidialSession.sessionId,
        phone_number: vicidialSession.phoneNumber,
        raw_response: vicidialSession.rawResponse,
      })

      try {
        const response = await axios.post<TelephonyVicidialActionResponse>(
          `${URL}${endpoint}`,
          {
            provider: 'VICIDIAL',
            ...body,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        )

        const normalizedActionStatus = clearVicidialCallContextIfIdle(response.data)
        if (normalizedActionStatus) {
          syncVicidialSessionState(normalizedActionStatus)
        }
        // Fire a status refresh, but don't keep the caller waiting on it. The
        // backend response already reflects the post-action state (including
        // any resume from the wrap-up pause on dispo), so blocking here just
        // adds 1-3 seconds of perceived "stuck" UI for no benefit.
        void refreshVicidialSession({silent: true}).then((refreshedStatus) => {
          syncVicidialDrawerDiagnostics(
            refreshedStatus || normalizedActionStatus || response.data,
            actionMessage,
            response.data.raw_response || ''
          )
        })
        toast.success(actionMessage)
        return response.data
      } catch (error: any) {
        const message = error?.response?.data?.error || actionErrorMessage
        const refreshedStatus = await refreshVicidialSession({silent: true})
        syncVicidialDrawerDiagnostics(
          refreshedStatus,
          message,
          error?.response?.data?.raw_response || ''
        )
        toast.error(message)
        return null
      }
    },
    [
      config.vicidial_agent_user,
      config.vicidial_enabled,
      getToken,
      refreshVicidialSession,
      syncVicidialDrawerDiagnostics,
      vicidialSession.campaignId,
      vicidialSession.pauseCode,
      vicidialSession.phoneNumber,
      vicidialSession.rawResponse,
      vicidialSession.sessionId,
      vicidialSession.status,
    ]
  )

  const setVicidialPause = useCallback(
    async (paused: boolean, pauseCode?: string) => {
      return runVicidialAction(
        '/api/telephony/vicidial/pause',
        {
          paused,
          pause_code: paused
            ? String(pauseCode || '')
                .trim()
                .toUpperCase()
                .slice(0, 6)
            : '',
        },
        paused ? 'Agent paused in PolicyDial.' : 'Agent set to ready in PolicyDial.',
        paused ? 'Unable to pause the PolicyDial agent.' : 'Unable to resume the PolicyDial agent.'
      )
    },
    [runVicidialAction]
  )

  const sendVicidialDtmf = useCallback(
    async (digits: string) => {
      return runVicidialAction(
        '/api/telephony/vicidial/dtmf',
        {digits},
        'DTMF sent through PolicyDial.',
        'Unable to send DTMF through PolicyDial.'
      )
    },
    [runVicidialAction]
  )

  const parkVicidialCall = useCallback(
    async (action: 'PARK_CUSTOMER' | 'GRAB_CUSTOMER') => {
      return runVicidialAction(
        '/api/telephony/vicidial/park',
        {action},
        action === 'PARK_CUSTOMER'
          ? 'Customer parked in PolicyDial.'
          : 'Customer returned to the agent in PolicyDial.',
        action === 'PARK_CUSTOMER'
          ? 'Unable to park the current PolicyDial call.'
          : 'Unable to grab the parked PolicyDial call.'
      )
    },
    [runVicidialAction]
  )

  const transferVicidialCall = useCallback(
    async (phoneNumber: string) => {
      return runVicidialAction(
        '/api/telephony/vicidial/transfer',
        {phone_number: phoneNumber},
        'Blind transfer sent through PolicyDial.',
        'Unable to blind transfer the PolicyDial call.'
      )
    },
    [runVicidialAction]
  )

  const setVicidialDisposition = useCallback(
    async ({
      statusCode,
      callbackDateTime,
      callbackComments,
      callbackType,
      qmDispoCode,
    }: SetVicidialDispositionInput) => {
      const normalizedStatusCode = String(statusCode || '')
        .trim()
        .toUpperCase()
      if (!normalizedStatusCode) {
        toast.error('A disposition code is required.')
        return null
      }

      // The backend already polls / retries to resume the agent after the
      // wrap-up pause that VICIdial sets on dispo. Doing a second resume from
      // here just doubles the latency and made the modal feel stuck.
      return runVicidialAction(
        '/api/telephony/vicidial/disposition',
        {
          status_code: normalizedStatusCode,
          callback_datetime: String(callbackDateTime || '').trim(),
          callback_comments: String(callbackComments || '').trim(),
          callback_type: String(callbackType || '').trim(),
          qm_dispo_code: String(qmDispoCode || '').trim(),
        },
        `Disposition ${normalizedStatusCode} saved in PolicyDial.`,
        'Unable to save the PolicyDial disposition.'
      )
    },
    [runVicidialAction]
  )

  const applyVicidialInboundGroups = useCallback(
    async (ingroups?: string[]) => {
      const token = getToken()
      if (!token) return null

      const nextGroups = (ingroups || selectedVicidialInboundGroups)
        .map((value) => String(value || '').trim())
        .filter(Boolean)

      if (!nextGroups.length) {
        toast.error('Select at least one inbound group.')
        return null
      }

      try {
        const response = await axios.post<TelephonyVicidialActionResponse>(
          `${URL}/api/telephony/vicidial/ingroups/change`,
          {
            provider: 'VICIDIAL',
            ingroups: nextGroups,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        )

        setVicidialInboundGroups(nextGroups)
        await delay(300)
        await refreshVicidialSession({silent: true})
        toast.success('Inbound groups updated in PolicyDial.')
        return response.data
      } catch (error: any) {
        const message =
          error?.response?.data?.error || 'Unable to update inbound groups in PolicyDial.'
        toast.error(message)
        return null
      }
    },
    [getToken, refreshVicidialSession, selectedVicidialInboundGroups, setVicidialInboundGroups]
  )

  useEffect(() => {
    if (!resolved || !config.vicidial_enabled || !config.vicidial_agent_user) {
      return
    }

    if (!localStorage.getItem(STORAGE_NAMES.VICIDIAL_SESSION)) {
      return
    }

    refreshVicidialSession({silent: true})
    const pollIntervalMs = hasVicidialCallContext(vicidialSession)
      ? 1500
      : vicidialSession.activeSession
      ? 3500
      : 10000
    const intervalId = window.setInterval(() => {
      refreshVicidialSession({silent: true})
    }, pollIntervalMs)

    return () => {
      window.clearInterval(intervalId)
    }
  }, [
    config.vicidial_agent_user,
    config.vicidial_enabled,
    refreshVicidialSession,
    resolved,
    vicidialSession.activeSession,
    vicidialSession.callId,
    vicidialSession.leadId,
    vicidialSession.phoneNumber,
    vicidialSession.realTimeSubStatus,
    vicidialSession.status,
    vicidialSession.vendorLeadCode,
  ])

  useEffect(() => {
    const handleVicidialSessionSync = (event: Event) => {
      const customEvent = event as CustomEvent<TelephonyVicidialSessionDetail>
      if (!customEvent.detail) return

      setVicidialSession((current) => {
        const nextCheckedAt = customEvent.detail.checkedAt || 0
        const currentCheckedAt = current.checkedAt || 0
        if (nextCheckedAt && currentCheckedAt && nextCheckedAt < currentCheckedAt) {
          return current
        }
        return customEvent.detail
      })
    }

    const handleVicidialLogin = () => {
      setVicidialSession((current) => ({
        ...current,
        connected: true,
      }))
    }
    const handleVicidialLogout = () => {
      localStorage.removeItem(STORAGE_NAMES.VICIDIAL_SESSION)
      localStorage.setItem(STORAGE_NAMES.VICIDIAL_FORCE_DISCONNECTED, '1')
      setVicidialSession(
        createVicidialSessionState(config, {
          connected: false,
          connecting: false,
          activeSession: false,
          status: 'LOGOUT',
          message: VICIDIAL_LOGGED_OUT_MESSAGE,
          rawResponse: '',
          checkedAt: Date.now(),
        })
      )
    }

    window.addEventListener(TELEPHONY_VICIDIAL_LOGOUT_EVENT, handleVicidialLogout)
    window.addEventListener(
      TELEPHONY_VICIDIAL_SESSION_EVENT,
      handleVicidialSessionSync as EventListener
    )
    window.addEventListener('TELEPHONY_VICIDIAL_LOGIN_EVENT', handleVicidialLogin)

    // Almacenar el handler para removerlo correctamente
    const storageHandler = (e: StorageEvent) => {
      if (e.key === STORAGE_NAMES.VICIDIAL_SESSION) {
        if (e.newValue) handleVicidialLogin()
        else handleVicidialLogout()
      }
    }
    window.addEventListener('storage', storageHandler)

    return () => {
      window.removeEventListener(TELEPHONY_VICIDIAL_LOGOUT_EVENT, handleVicidialLogout)
      window.removeEventListener(
        TELEPHONY_VICIDIAL_SESSION_EVENT,
        handleVicidialSessionSync as EventListener
      )
      window.removeEventListener('TELEPHONY_VICIDIAL_LOGIN_EVENT', handleVicidialLogin)
      window.removeEventListener('storage', storageHandler)
    }
  }, [config])

  const startCall = useCallback(
    async ({phone, leadId, autoOpenWebphone = true, providerOverride}: StartTelephonyCallInput) => {
      const selectedProvider = providerOverride || config.provider
      if (!phone) {
        toast.error('A phone number is required.')
        return null
      }

      if (selectedProvider === 'VICIDIAL') {
        const token = getToken()
        if (!token) return null

        if (!config.vicidial_enabled) {
          toast.error('PolicyDial is not enabled for this account.')
          return null
        }

        if (!config.vicidial_agent_user) {
          toast.error('No PolicyDial agent user is configured for this account.')
          return null
        }

        if (autoOpenWebphone) {
          const sessionStatus = await connectVicidialSession()
          if (!sessionStatus && !vicidialSession.connected) {
            return null
          }
        }

        const effectiveCampaignId = getEffectiveVicidialCampaignId()
        const preflightStatus = await getAgentStatus('VICIDIAL')
        if (
          preflightStatus?.active_session &&
          !vicidialSessionMatchesCampaign(preflightStatus, effectiveCampaignId)
        ) {
          const message = effectiveCampaignId
            ? `PolicyDial is connected to campaign ${
                preflightStatus?.campaign_id || 'unknown'
              }. Reconnect it with campaign ${effectiveCampaignId} before dialing.`
            : 'PolicyDial is connected to a different campaign. Reconnect it before dialing.'
          syncVicidialDrawerDiagnostics(preflightStatus, message)
          toast.error(message)
          return null
        }
        syncVicidialDrawerDiagnostics(
          preflightStatus,
          preflightStatus?.active_session
            ? 'PolicyDial agent session detected. Sending the call request...'
            : 'PolicyDial agent session is not active yet. The backend will still try to connect it before dialing.'
        )

        try {
          const response = await axios.post<TelephonyStartResponse>(
            `${URL}/api/telephony/start`,
            {
              phone,
              lead_id: leadId || null,
              provider: 'VICIDIAL',
              ...(effectiveCampaignId ? {campaign_id: effectiveCampaignId} : {}),
            },
            {
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
            }
          )
          syncVicidialDrawerDiagnostics(
            {
              ...preflightStatus,
              agent_user: response.data.agent_user || preflightStatus?.agent_user,
              status: response.data.session_status?.status || preflightStatus?.status,
              campaign_id:
                response.data.session_status?.campaign_id || preflightStatus?.campaign_id,
              session_id: response.data.session_status?.session_id || preflightStatus?.session_id,
              phone_number:
                response.data.session_status?.phone_number ||
                response.data.normalized_phone ||
                phone ||
                preflightStatus?.phone_number,
              raw_response:
                response.data.raw_response ||
                response.data.session_raw_response ||
                preflightStatus?.raw_response,
              active_session: true,
              provider: 'VICIDIAL',
            },
            `Call request sent for ${response.data.normalized_phone || phone}.`
          )
          syncVicidialSessionState(
            {
              provider: 'VICIDIAL',
              active_session: true,
              agent_user:
                response.data.agent_user ||
                preflightStatus?.agent_user ||
                config.vicidial_agent_user,
              status: response.data.session_status?.status || preflightStatus?.status || '',
              call_id: response.data.external_call_id || preflightStatus?.call_id || '',
              policytools_call_id: response.data.call_id ?? null,
              lead_id: String(response.data.lead_id || preflightStatus?.lead_id || ''),
              campaign_id:
                response.data.session_status?.campaign_id ||
                preflightStatus?.campaign_id ||
                effectiveCampaignId,
              pause_code:
                response.data.session_status?.status === 'PAUSED'
                  ? preflightStatus?.pause_code || ''
                  : '',
              real_time_sub_status: preflightStatus?.real_time_sub_status || '',
              phone_number:
                response.data.session_status?.phone_number ||
                response.data.normalized_phone ||
                phone ||
                preflightStatus?.phone_number,
              vendor_lead_code:
                response.data.vendor_lead_code || preflightStatus?.vendor_lead_code || '',
              first_name: response.data.first_name || preflightStatus?.first_name || '',
              last_name: response.data.last_name || preflightStatus?.last_name || '',
              email: response.data.email || preflightStatus?.email || '',
              session_id:
                response.data.session_status?.session_id || preflightStatus?.session_id || '',
              raw_response:
                response.data.raw_response ||
                response.data.session_raw_response ||
                preflightStatus?.raw_response ||
                '',
            },
            {
              localCallId: response.data.call_id ?? null,
              message: `Call request sent for ${response.data.normalized_phone || phone}.`,
            }
          )
          toast.success('Call sent to PolicyDial.')
          return response.data
        } catch (error: any) {
          const message = error?.response?.data?.error || 'Unable to start the PolicyDial call.'
          const statusAfterError = await getAgentStatus('VICIDIAL')
          syncVicidialDrawerDiagnostics(
            statusAfterError,
            message,
            error?.response?.data?.raw_response || ''
          )
          toast.error(message)
          return null
        }
      }

      const isRingConnected = Boolean(localStorage.getItem(STORAGE_NAMES.RING_CENTRAL))
      if (!isRingConnected) {
        toast.error('RingCentral is not connected in this browser.')
        return null
      }

      if (!(window as any).RCAdapter?.clickToCall) {
        toast.error('RingCentral is still loading. Try again in a moment.')
        return null
      }

      ;(window as any).RCAdapter.clickToCall(formatPhoneNumber(phone), true)
      toast.success('Call sent to RingCentral.')
      return {provider: 'RINGCENTRAL'} as TelephonyStartResponse
    },
    [
      config,
      connectVicidialSession,
      getAgentStatus,
      getEffectiveVicidialCampaignId,
      getToken,
      syncVicidialDrawerDiagnostics,
      vicidialSession.connected,
    ]
  )

  const hangupCall = useCallback(
    async (providerOverride?: TelephonyProviderType, callId?: number | string | null) => {
      const selectedProvider = providerOverride || config.provider
      if (selectedProvider !== 'VICIDIAL') {
        toast.error('Hangup is only implemented for PolicyDial.')
        return null
      }

      const token = getToken()
      if (!token) return null

      try {
        const response = await axios.post<TelephonyHangupResponse>(
          `${URL}/api/telephony/hangup`,
          {
            provider: 'VICIDIAL',
            call_id: callId || null,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        )

        await delay(350)
        const refreshedStatus = clearVicidialCallContextIfIdle(
          await refreshVicidialSession({silent: true})
        )
        if (refreshedStatus) {
          syncVicidialSessionState(refreshedStatus)
        }
        syncVicidialDrawerDiagnostics(
          refreshedStatus,
          'PolicyDial hangup request sent.',
          response.data.raw_response || ''
        )
        toast.success('PolicyDial hangup request sent.')
        return response.data
      } catch (error: any) {
        const message = error?.response?.data?.error || 'Unable to hang up the PolicyDial call.'
        const refreshedStatus = clearVicidialCallContextIfIdle(
          await refreshVicidialSession({silent: true})
        )
        if (refreshedStatus) {
          syncVicidialSessionState(refreshedStatus)
        }
        syncVicidialDrawerDiagnostics(
          refreshedStatus,
          message,
          error?.response?.data?.raw_response || ''
        )
        toast.error(message)
        return null
      }
    },
    [
      config.provider,
      getToken,
      refreshVicidialSession,
      syncVicidialDrawerDiagnostics,
      syncVicidialSessionState,
    ]
  )

  const prepareVicidialDial = useCallback(
    (phone: string, leadId?: number | string | null, autoDial = true) => {
      const normalizedPhone = String(phone || '').trim()
      if (!normalizedPhone) {
        toast.error('A phone number is required.')
        return false
      }

      dispatchVicidialPrepareDial({
        phone: normalizedPhone,
        leadId: leadId ?? null,
        autoDial,
      })
      return true
    },
    []
  )

  return {
    config,
    loading,
    resolved,
    resolveConfig,
    getAgentStatus,
    openWebphone,
    startCall,
    hangupCall,
    prepareVicidialDial,
    vicidialSession,
    vicidialCampaignOptions,
    loadingVicidialCampaigns,
    loadVicidialCampaigns,
    setVicidialCampaign,
    vicidialInboundGroupOptions,
    loadingVicidialInboundGroups,
    selectedVicidialInboundGroups,
    setVicidialInboundGroups,
    loadVicidialInboundGroups,
    applyVicidialInboundGroups,
    refreshVicidialSession,
    connectVicidialSession,
    disconnectVicidialSession,
    setVicidialPause,
    sendVicidialDtmf,
    parkVicidialCall,
    transferVicidialCall,
    setVicidialDisposition,
    isRingCentralConnected: Boolean(localStorage.getItem(STORAGE_NAMES.RING_CENTRAL)),
  }
}

type TelephonyContextValue = ReturnType<typeof useTelephonyController>

const TelephonyContext = createContext<TelephonyContextValue | null>(null)

export const TelephonyProvider = ({children}: {children: ReactNode}) => {
  const value = useTelephonyController()
  return createElement(TelephonyContext.Provider, {value}, children)
}

export const useTelephony = () => {
  const context = useContext(TelephonyContext)
  if (!context) {
    throw new Error('useTelephony must be used within TelephonyProvider')
  }
  return context
}
